import { MarketingData } from "@alba-cars/common-modules";
import Cookies from "js-cookie";

import { OPENAI_UTM_SOURCES } from "@/utils/constants";

const COOKIE_KEY = "utm_campaigns";
const COOKIE_EXPIRY_DAYS = 14;
const META_SOURCES = ["facebook", "instagram", "messenger", "audience_network", "whatsapp"];

const isOpenAIUtmSource = (source: string | null) =>
  Boolean(source && (OPENAI_UTM_SOURCES as readonly string[]).includes(source.toLowerCase()));

const getGenericUtmData = (params: URLSearchParams): NonNullable<MarketingData["Other"]> => ({
  utm_source: params.get("utm_source") ?? "",
  utm_medium: params.get("utm_medium") ?? "",
  utm_campaign: params.get("utm_campaign") ?? "",
  utm_term: params.get("utm_term") ?? "",
  utm_content: params.get("utm_content") ?? "",
});

const hasGenericUtmParams = (params: URLSearchParams) =>
  Boolean(
    params.get("utm_source") ||
    params.get("utm_medium") ||
    params.get("utm_campaign") ||
    params.get("utm_term") ||
    params.get("utm_content"),
  );

export const readUTMCookies = (): MarketingData => {
  const stored = Cookies.get(COOKIE_KEY);
  if (!stored) return {};
  try {
    const utmCampaigns = JSON.parse(stored) as { data: MarketingData; timestamp: number }[];

    const currentTime = new Date().getTime();
    const validCampaigns = utmCampaigns.filter((campaign) => {
      const campaignAge = currentTime - campaign.timestamp;
      const campaignAgeInDays = campaignAge / (1000 * 60 * 60 * 24);
      return campaignAgeInDays <= COOKIE_EXPIRY_DAYS;
    });

    if (validCampaigns.length === 0) return {};

    return validCampaigns.reduce((combined, campaign) => {
      if (campaign.data.Meta && !combined.Meta) {
        combined.Meta = { ...campaign.data.Meta };
      }

      if (campaign.data.Google && !combined.Google) {
        combined.Google = { ...campaign.data.Google };
      }

      if (campaign.data.Bing && !combined.Bing) {
        combined.Bing = { ...campaign.data.Bing };
      }

      if (campaign.data.Brevo && !combined.Brevo) {
        combined.Brevo = { ...campaign.data.Brevo };
      }

      if (campaign.data.X && !combined.X) {
        combined.X = { ...campaign.data.X };
      }

      if (campaign.data.Broadcast && !combined.Broadcast) {
        combined.Broadcast = { ...campaign.data.Broadcast };
      }

      if (campaign.data.OpenAI && !combined.OpenAI) {
        combined.OpenAI = { ...campaign.data.OpenAI };
      }

      if (campaign.data.Other && !combined.Other) {
        // Migrate older OpenAI cookies that were stored under Other
        if (!combined.OpenAI && isOpenAIUtmSource(campaign.data.Other.utm_source)) {
          combined.OpenAI = { ...campaign.data.Other };
        } else {
          combined.Other = { ...campaign.data.Other };
        }
      }

      return combined;
    }, {} as MarketingData);
  } catch (e) {
    return {};
  }
};

export const storeUTMInCookies = () => {
  const params = new URLSearchParams(window.location.search);
  const newCampaign: MarketingData = {};
  const isBroadcast =
    params.get("utm_source")?.toLowerCase() === "broadcast" ||
    params.get("utm_medium")?.toLowerCase() === "broadcast";

  if (params.get("gclid") || params.get("utm_source")?.toLowerCase() === "google") {
    newCampaign.Google = {
      gclid: params.get("gclid") ?? "",
      utm_source: params.get("utm_source") ?? "google",
      utm_medium: params.get("utm_medium") ?? "",
      utm_campaign: params.get("utm_campaign") ?? "",
      utm_term: params.get("utm_term") ?? "",
      utm_content: params.get("utm_content") ?? "",
    };
  }

  if (
    !isBroadcast &&
    (params.get("fbclid") ||
      META_SOURCES.includes(params?.get("utm_source")?.toLowerCase() || "") ||
      params.get("utm_source")?.toLowerCase() === "facebook" ||
      params.get("utm_source")?.toLowerCase() === "instagram")
  ) {
    newCampaign.Meta = {
      utm_source: params.get("utm_source") ?? "",
      utm_medium: params.get("utm_medium") ?? "",
      utm_campaign: params.get("utm_campaign") ?? "",
      utm_content: params.get("utm_content") ?? "",
    };
  }

  if (params.get("msclkid")) {
    newCampaign.Bing = {
      msclkid: params.get("msclkid")!,
      utm_source: params.get("utm_source") ?? "bing",
      utm_medium: params.get("utm_medium") ?? "",
      utm_campaign: params.get("utm_campaign") ?? "",
      utm_term: params.get("utm_term") ?? "",
      utm_content: params.get("utm_content") ?? "",
    };
  }

  if (params.get("utm_source")?.toLowerCase() === "brevo") {
    newCampaign.Brevo = {
      utm_source: params.get("utm_source") ?? "brevo",
      utm_campaign: params.get("utm_campaign") ?? "",
      utm_medium: params.get("utm_medium") ?? "",
      utm_id: params.get("utm_id") ?? "",
      utm_content: decodeURIComponent(params.get("utm_content") ?? ""),
    };
  }

  if (params.get("utm_source")?.toLowerCase() === "x") {
    newCampaign.X = {
      utm_source: params.get("utm_source") ?? "x",
      utm_campaign: params.get("utm_campaign") ?? "",
      utm_medium: params.get("utm_medium") ?? "",
      utm_content: decodeURIComponent(params.get("utm_content") ?? ""),
    };
  }

  if (isBroadcast) {
    newCampaign.Broadcast = getGenericUtmData(params);
  }

  if (isOpenAIUtmSource(params.get("utm_source"))) {
    newCampaign.OpenAI = getGenericUtmData(params);
  }

  if (hasGenericUtmParams(params) && Object.keys(newCampaign).length === 0) {
    newCampaign.Other = getGenericUtmData(params);
  }

  if (Object.keys(newCampaign).length === 0) return;

  let existing: { data: MarketingData; timestamp: number }[] = [];
  try {
    existing = JSON.parse(Cookies.get(COOKIE_KEY) || "[]");
  } catch (e) {
    existing = [];
  }

  const campaignWithTimestamp = {
    data: newCampaign,
    timestamp: new Date().getTime(),
  };

  const campaignAlreadyStored = existing.some((c) => JSON.stringify(c.data) === JSON.stringify(newCampaign));

  if (!campaignAlreadyStored) {
    const currentTime = new Date().getTime();
    const validExistingCampaigns = existing.filter((campaign) => {
      const campaignAge = currentTime - campaign.timestamp;
      const campaignAgeInDays = campaignAge / (1000 * 60 * 60 * 24);
      return campaignAgeInDays <= COOKIE_EXPIRY_DAYS;
    });

    const updated = [...validExistingCampaigns, campaignWithTimestamp];
    Cookies.set(COOKIE_KEY, JSON.stringify(updated), { expires: COOKIE_EXPIRY_DAYS });
  }
};
